import { useQuery } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"

import { api } from "@/api/cliente"
import { formatearImporte } from "@/componentes/finanzas/PanelRubros"
import { type FilaSaldo, TablaSaldos } from "@/componentes/finanzas/TablaSaldos"

/**
 * Estado de cuenta del importador: lo que debe y lo que ya pagó.
 *
 * ⚠ **Aquí no aparece `costo_proveedor` ni ningún gasto administrativo, y esta
 * pantalla no es quien lo garantiza.** Lo garantiza la lista blanca de campos del
 * esquema del portal en el backend, y `test_portal_finanzas.py` lo comprueba
 * inspeccionando el payload — porque una fuga de margen no se detecta mirando la
 * pantalla: se detecta abriendo las herramientas de desarrollo, que es exactamente
 * lo que hace cualquiera con curiosidad.
 *
 * Este tipo `CxcDelPortal` es deliberadamente estrecho. Si el backend empezara a
 * mandar más campos, esta pantalla seguiría sin pintarlos, pero **el problema ya
 * habría ocurrido**: el dato viajó. Por eso la garantía está allá y no aquí.
 */

type CxcDelPortal = {
  id: number
  concepto: string
  monto: string
  monto_cobrado: string
  saldo: string
  moneda: "USD" | "BS"
  estado: string
  fecha_vencimiento: string | null
}

export function MiEstadoCuenta() {
  const consulta = useQuery({
    queryKey: ["mi-estado-cuenta"],
    queryFn: () => api.get<CxcDelPortal[]>("/api/v1/cxc"),
  })

  if (consulta.isPending) {
    return <p className="p-6">Cargando…</p>
  }
  if (consulta.isError) {
    return (
      <p className="flex items-center gap-2 p-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} aria-hidden={true} /> No se pudo cargar tu estado de cuenta.
      </p>
    )
  }

  const filas: FilaSaldo[] = consulta.data.map((c) => ({
    id: c.id,
    concepto: c.concepto,
    monto: c.monto,
    aplicado: c.monto_cobrado,
    moneda: c.moneda,
    saldada: c.estado === "COBRADO",
    anulada: c.estado === "ANULADA",
  }))

  const pendiente = consulta.data
    .filter((c) => c.estado !== "COBRADO" && c.estado !== "ANULADA")
    .reduce((suma, c) => suma + Number(c.saldo), 0)
    .toFixed(2)

  return (
    <section className="space-y-4 p-6">
      <h1 className="text-lg font-medium">Mi estado de cuenta</h1>
      {/* La cifra titular del portal, y era el único bloque de esta pantalla sin
          caja — apoyada sobre el lienzo encima de una tabla que sí la tiene. */}
      <p className="tarjeta flex items-baseline gap-2">
        <span style={{ color: "var(--fg-muted)" }}>Total pendiente</span>
        {/* Sin acción de cobro: el importador consulta, no cobra. */}
        <output className="text-xl font-semibold tabular-nums">
          {formatearImporte(pendiente, "USD", "")}
        </output>
      </p>
      <TablaSaldos filas={filas} />
    </section>
  )
}
